import {
  type DeleteUnpublish,
  unpublishQuery,
  LOOKUP_IDS_MAX,
  type LibraryPage,
  LibraryPageSchema,
  type LibraryQueryInput,
  type DashboardCheck,
  DashboardCheckSchema,
  type CalendarFeed,
  CalendarFeedSchema,
  apiPath,
  type CompileResult,
  type ContentDetachResult,
  ContentDetachResultSchema,
  type ContentReplaceRequest,
  type ContentReplaceResult,
  ContentReplaceResultSchema,
  type Interaction,
  InteractionSchema,
  type PlayerInteractions,
  PlayerInteractionsSchema,
  type UpsertInteractionRequestInput,
  type PlayerSettings,
  type PlayerSettingsOverrides,
  PlayerSettingsSchema,
  type ContentFolder,
  ContentFolderSchema,
  type CreateFolderRequestInput,
  type MoveContentRequestInput,
  type UpdateContentTagsRequestInput,
  type UpdateFolderRequest,
  CompileResultSchema,
  type CreateMediaAssetRequest,
  type Layout,
  LayoutSchema,
  type LayoutTemplate,
  LayoutTemplateSchema,
  type MediaAssetRecord,
  type RssPreview,
  RssPreviewSchema,
  MediaAssetRecordSchema,
  type Playlist,
  PlaylistSchema,
  type ScreenGroup,
  type BuildingInfo,
  ScreenGroupSchema,
  type ScreenSummary,
  ScreenSummarySchema,
  type UpdateMediaAssetRequest,
  type UpdateScreenRequest,
  type UpsertLayoutRequest,
  type UpsertLayoutTemplateRequest,
  type UpsertPlaylistRequestInput,
} from '@codot/contracts';
import { request } from './http';

/** Phase 16 Step 11: interactions on a player or a campaign (each change recompiles the players reached). */
export const interactionsApi = {
  forScreen: (screenId: string): Promise<PlayerInteractions> =>
    request('GET', apiPath('screenInteractions', { screenId }), PlayerInteractionsSchema),
  forLayout: (layoutId: string): Promise<Interaction[]> =>
    request('GET', apiPath('layoutInteractions', { layoutId }), InteractionSchema.array()),
  create: (owner: { kind: 'player' | 'campaign'; id: string }, body: UpsertInteractionRequestInput): Promise<Interaction> =>
    owner.kind === 'player'
      ? request('POST', apiPath('screenInteractions', { screenId: owner.id }), InteractionSchema, body)
      : request('POST', apiPath('layoutInteractions', { layoutId: owner.id }), InteractionSchema, body),
  update: (interactionId: string, body: UpsertInteractionRequestInput): Promise<Interaction> =>
    request('PUT', apiPath('interaction', { interactionId }), InteractionSchema, body),
  remove: (interactionId: string): Promise<unknown> => request('DELETE', apiPath('interaction', { interactionId }), null),
};

/** Phase 16 Step 6: library folders, Move and content tags (organising only; nothing recompiles). */
export const foldersApi = {
  list: (): Promise<ContentFolder[]> => request('GET', apiPath('folders'), ContentFolderSchema.array()),
  create: (body: CreateFolderRequestInput): Promise<ContentFolder> =>
    request('POST', apiPath('folders'), ContentFolderSchema, body),
  update: (folderId: string, body: UpdateFolderRequest): Promise<ContentFolder> =>
    request('PATCH', apiPath('folder', { folderId }), ContentFolderSchema, body),
  remove: (folderId: string): Promise<unknown> => request('DELETE', apiPath('folder', { folderId }), null),
  move: (body: MoveContentRequestInput): Promise<unknown> => request('POST', apiPath('contentMove'), null, body),
  tags: (body: UpdateContentTagsRequestInput): Promise<unknown> => request('POST', apiPath('contentTags'), null, body),
};

/** Phase 5 authoring API (groups, layouts, media library, publishing). */
export const groupsApi = {
  list: (): Promise<ScreenGroup[]> => request('GET', apiPath('groups'), ScreenGroupSchema.array()),
  create: (name: string, building: BuildingInfo | null = null): Promise<ScreenGroup> => request('POST', apiPath('groups'), ScreenGroupSchema, { name, building }),
  /** Ad-network pack: make it a building (details) or an ordinary group (null). */
  /** The group's Player Settings (between the organization defaults and each player's own). */
  setSettings: (groupId: string, settings: PlayerSettingsOverrides): Promise<ScreenGroup> =>
    request('PUT', apiPath('groupSettings', { groupId }), ScreenGroupSchema, settings),
  setBuilding: (groupId: string, name: string, building: BuildingInfo | null): Promise<ScreenGroup> =>
    request('PATCH', apiPath('group', { groupId }), ScreenGroupSchema, { name, building }),
  rename: (groupId: string, name: string): Promise<ScreenGroup> =>
    request('PATCH', apiPath('group', { groupId }), ScreenGroupSchema, { name }),
  remove: (groupId: string): Promise<unknown> => request('DELETE', apiPath('group', { groupId }), null),
  /** Phase 17.7: Synchronized Playback on (with its lead player) or off. */
  setSync: (groupId: string, enabled: boolean, leadScreenId: string | null): Promise<ScreenGroup> =>
    request('PUT', apiPath('groupSync', { groupId }), ScreenGroupSchema, { enabled, leadScreenId }),
};

export const layoutsApi = {
  list: (): Promise<Layout[]> => request('GET', apiPath('layouts'), LayoutSchema.array()),
  create: (body: UpsertLayoutRequest): Promise<Layout> => request('POST', apiPath('layouts'), LayoutSchema, body),
  update: (layoutId: string, body: UpsertLayoutRequest): Promise<Layout> =>
    request('PUT', apiPath('layout', { layoutId }), LayoutSchema, body),
  /** `force`: also take it out of every campaign, playlist and player that uses it. */
  remove: (layoutId: string, force = false, unpublish: DeleteUnpublish = []): Promise<unknown> =>
    request('DELETE', `${apiPath('layout', { layoutId })}${force ? '?force=true' : ''}${force && unpublish.length ? `&unpublish=${unpublishQuery(unpublish)}` : ''}`, null),
};

/** Phase 16: an organization's custom layouts (built-ins live in utils/layouts.ts). */
export const layoutTemplatesApi = {
  list: (): Promise<LayoutTemplate[]> => request('GET', apiPath('layoutTemplates'), LayoutTemplateSchema.array()),
  create: (body: UpsertLayoutTemplateRequest): Promise<LayoutTemplate> =>
    request('POST', apiPath('layoutTemplates'), LayoutTemplateSchema, body),
  update: (templateId: string, body: UpsertLayoutTemplateRequest): Promise<LayoutTemplate> =>
    request('PUT', apiPath('layoutTemplate', { templateId }), LayoutTemplateSchema, body),
  remove: (templateId: string): Promise<unknown> => request('DELETE', apiPath('layoutTemplate', { templateId }), null),
};

/** The Content page, a page at a time (search, filters and sort run on the server). */
export const libraryApi = {
  page: (q: LibraryQueryInput): Promise<LibraryPage> => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '' && v !== 'all') p.set(k, String(v));
    return request('GET', `${apiPath('contentLibrary')}?${p}`, LibraryPageSchema);
  },
};

export const mediaApi = {
  /** Put another item in this one's place everywhere (`only`: just those rows, for Undo). */
  replace: (body: ContentReplaceRequest): Promise<ContentReplaceResult> => request('POST', apiPath('contentReplace'), ContentReplaceResultSchema, body),
  /** Take it out of every campaign, playlist and player; it stays in the library. */
  detach: (assetId: string): Promise<ContentDetachResult> => request('POST', apiPath('contentDetach'), ContentDetachResultSchema, { assetId }),
  /** The whole library in one response. Prefer libraryApi (pages) or byIds (lookups). */
  list: (): Promise<MediaAssetRecord[]> => request('GET', apiPath('mediaAssets'), MediaAssetRecordSchema.array()),
  /** These items (any count; asked for in chunks of LOOKUP_IDS_MAX). Unknown or unseen ids are left out. */
  byIds: async (ids: readonly string[]): Promise<MediaAssetRecord[]> => {
    const unique = [...new Set(ids)];
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += LOOKUP_IDS_MAX) chunks.push(unique.slice(i, i + LOOKUP_IDS_MAX));
    const parts = await Promise.all(chunks.map((c) => request('GET', `${apiPath('mediaAssets')}?ids=${c.join(',')}`, MediaAssetRecordSchema.array())));
    return parts.flat();
  },
  get: (assetId: string): Promise<MediaAssetRecord> => request('GET', apiPath('mediaAsset', { assetId }), MediaAssetRecordSchema),
  create: (body: CreateMediaAssetRequest): Promise<MediaAssetRecord> =>
    request('POST', apiPath('mediaAssets'), MediaAssetRecordSchema, body),
  update: (assetId: string, body: UpdateMediaAssetRequest): Promise<MediaAssetRecord> =>
    request('PATCH', apiPath('mediaAsset', { assetId }), MediaAssetRecordSchema, body),
  /** `force`: also take it out of every campaign, playlist and player that uses it. */
  remove: (assetId: string, force = false, unpublish: DeleteUnpublish = []): Promise<unknown> =>
    request('DELETE', `${apiPath('mediaAsset', { assetId })}${force ? '?force=true' : ''}${force && unpublish.length ? `&unpublish=${unpublishQuery(unpublish)}` : ''}`, null),
  /** First headlines of a feed, for the Customize App live preview. */
  /** BI dashboard: can this link be previewed and shown (reachable, no sign-in, allowed in a frame)? */
  dashboardCheck: (url: string): Promise<DashboardCheck> => request('GET', `${apiPath('dashboardCheck')}?url=${encodeURIComponent(url)}`, DashboardCheckSchema),
  /** Calendar app: the API reads and expands the iCal link (browsers can't fetch most calendars). */
  calendarPreview: (url: string, days: number): Promise<CalendarFeed> =>
    request('GET', `${apiPath('calendarPreview')}?url=${encodeURIComponent(url)}&days=${days}`, CalendarFeedSchema),
  rssPreview: (url: string): Promise<RssPreview> =>
    request('GET', `${apiPath('rssPreview')}?url=${encodeURIComponent(url)}`, RssPreviewSchema),
};

export const screenSettingsApi = {
  update: (screenId: string, body: UpdateScreenRequest): Promise<ScreenSummary> =>
    request('PATCH', apiPath('screen', { screenId }), ScreenSummarySchema, body),
  publish: (screenId: string): Promise<CompileResult> =>
    request('POST', apiPath('screenPublish', { screenId }), CompileResultSchema),
  /** Phase 16 Step 8: this player's overrides (missing keys inherit the organization defaults). */
  setSettings: (screenId: string, overrides: PlayerSettingsOverrides): Promise<ScreenSummary> =>
    request('PUT', apiPath('screenSettings', { screenId }), ScreenSummarySchema, overrides),
  /** Phase 16 Step 8: organization defaults (every player is recompiled). */
  setDefaults: (defaults: PlayerSettings): Promise<PlayerSettings> =>
    request('PUT', apiPath('playerDefaults'), PlayerSettingsSchema, defaults),
};

/** Phase 16 Step 4: playlists (saved as a whole). */
export const playlistsApi = {
  list: (): Promise<Playlist[]> => request('GET', apiPath('playlists'), PlaylistSchema.array()),
  get: (playlistId: string): Promise<Playlist> => request('GET', apiPath('playlist', { playlistId }), PlaylistSchema),
  create: (body: UpsertPlaylistRequestInput): Promise<Playlist> =>
    request('POST', apiPath('playlists'), PlaylistSchema, body),
  update: (playlistId: string, body: UpsertPlaylistRequestInput): Promise<Playlist> =>
    request('PUT', apiPath('playlist', { playlistId }), PlaylistSchema, body),
  /** `force`: also take it out of every campaign, playlist and player that uses it. */
  remove: (playlistId: string, force = false, unpublish: DeleteUnpublish = []): Promise<unknown> =>
    request('DELETE', `${apiPath('playlist', { playlistId })}${force ? '?force=true' : ''}${force && unpublish.length ? `&unpublish=${unpublishQuery(unpublish)}` : ''}`, null),
};
